import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt } from "../electrical/electrical-helpers";

const ResultCard = ({ label, value, unit, accent }: { label: string; value: string; unit: string; accent?: boolean }) => (
  <div className="trade-card flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono-data text-lg font-bold ${accent ? "text-primary" : "text-foreground"}`}>
      {value} {unit && <span className="text-xs text-muted-foreground font-normal">{unit}</span>}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════
   VOLTAGE DROP CALCULATOR
   ═══════════════════════════════════════════════════════ */

// Wire resistance (Ω per 1000 ft, copper 75°C) by AWG
const WIRE_RESISTANCE: { awg: string; ohms: number; kcmil: number }[] = [
  { awg: "14", ohms: 3.14, kcmil: 4.11 },
  { awg: "12", ohms: 1.98, kcmil: 6.53 },
  { awg: "10", ohms: 1.24, kcmil: 10.38 },
  { awg: "8", ohms: 0.778, kcmil: 16.51 },
  { awg: "6", ohms: 0.491, kcmil: 26.24 },
  { awg: "4", ohms: 0.308, kcmil: 41.74 },
  { awg: "3", ohms: 0.245, kcmil: 52.62 },
  { awg: "2", ohms: 0.194, kcmil: 66.36 },
  { awg: "1", ohms: 0.154, kcmil: 83.69 },
  { awg: "1/0", ohms: 0.122, kcmil: 105.6 },
  { awg: "2/0", ohms: 0.0967, kcmil: 133.1 },
  { awg: "3/0", ohms: 0.0766, kcmil: 167.8 },
  { awg: "4/0", ohms: 0.0608, kcmil: 211.6 },
  { awg: "250", ohms: 0.0515, kcmil: 250 },
  { awg: "300", ohms: 0.0429, kcmil: 300 },
  { awg: "350", ohms: 0.0367, kcmil: 350 },
  { awg: "500", ohms: 0.0258, kcmil: 500 },
];

export const VoltageDropCalc = () => {
  const [wireIdx, setWireIdx] = useState("1"); // default 12 AWG
  const [amps, setAmps] = useState("");
  const [distance, setDistance] = useState("");
  const [voltage, setVoltage] = useState("120");
  const [phase, setPhase] = useState<"1" | "3">("1");

  const wire = WIRE_RESISTANCE[parseInt(wireIdx)];
  const I = parseFloat(amps);
  const D = parseFloat(distance);
  const V = parseFloat(voltage);
  const hasInput = I > 0 && D > 0 && V > 0;

  // VD = (2 × R × I × D) / 1000 for single phase
  // VD = (√3 × R × I × D) / 1000 for three phase
  const multiplier = phase === "1" ? 2 : Math.sqrt(3);
  const vDrop = hasInput ? (multiplier * wire.ohms * I * D) / 1000 : 0;
  const vDropPct = hasInput ? (vDrop / V) * 100 : 0;
  const vAtLoad = V - vDrop;

  // Find minimum wire size for 3% drop
  const minWire = useMemo(() => {
    if (!hasInput) return null;
    for (const w of WIRE_RESISTANCE) {
      const drop = (multiplier * w.ohms * I * D) / 1000;
      const pct = (drop / V) * 100;
      if (pct <= 3) return { awg: w.awg, pct };
    }
    return null;
  }, [hasInput, I, D, V, multiplier]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">voltage drop</span> for copper conductors. NEC recommends ≤3% for branch circuits and ≤5% total (branch + feeder).
        </p>
      </div>
      <div className="space-y-3">
        <div className="flex gap-2">
          <button onClick={() => setPhase("1")} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${phase === "1" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>1-Phase</button>
          <button onClick={() => setPhase("3")} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${phase === "3" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>3-Phase</button>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Wire Size</label>
          <Select value={wireIdx} onValueChange={setWireIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WIRE_RESISTANCE.map((w, i) => (
                <SelectItem key={i} value={String(i)}>{w.awg} AWG ({w.kcmil} kcmil)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Amps</label>
            <Input type="number" inputMode="decimal" placeholder="A…" value={amps} onChange={(e) => setAmps(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Distance (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="ft…" value={distance} onChange={(e) => setDistance(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Voltage</label>
            <Select value={voltage} onValueChange={setVoltage}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="120">120V</SelectItem>
                <SelectItem value="208">208V</SelectItem>
                <SelectItem value="240">240V</SelectItem>
                <SelectItem value="277">277V</SelectItem>
                <SelectItem value="480">480V</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Voltage Drop" value={fmt(vDrop, 2)} unit="V" accent />
          <ResultCard label="Drop %" value={fmt(vDropPct, 2)} unit="%" accent />
          <ResultCard label="Voltage at Load" value={fmt(vAtLoad, 1)} unit="V" />
          {vDropPct > 3 && vDropPct <= 5 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Exceeds 3% NEC recommendation for branch circuits.</p>
            </div>
          )}
          {vDropPct > 5 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
              <p className="text-xs text-destructive font-semibold">⚠️ Exceeds 5% total NEC recommendation. Upsize conductors.</p>
            </div>
          )}
          {minWire && vDropPct > 3 && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-3">
              <p className="text-xs text-primary font-semibold">💡 Min. wire for ≤3% drop: {minWire.awg} AWG ({fmt(minWire.pct, 2)}%)</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CONDUIT BEND RADIUS REFERENCE
   ═══════════════════════════════════════════════════════ */

export const bendRadiusHeaders = ["Trade Size", "EMT", "IMC", "RMC", "PVC"];
export const bendRadiusRows: (string | number)[][] = [
  ['½"',    4,   4,   4,   "—"],
  ['¾"',    5,   5,   5,   "—"],
  ['1"',    6,   6,   6,    10],
  ['1¼"',   8,   8,   8,    12],
  ['1½"',  10,  10,  10,    14],
  ['2"',   12,  12,  12,    18],
  ['2½"',  15,  15,  15,    24],
  ['3"',   18,  18,  18,    30],
  ['3½"',  21,  21,  21,    36],
  ['4"',   24,  24,  24,    36],
  ['5"',   30,  30,  30,    48],
  ['6"',   36,  36,  36,    60],
];

/* ═══════════════════════════════════════════════════════
   LIGHTING LOAD CALCULATOR (NEC Table 220.12)
   ═══════════════════════════════════════════════════════ */

const LIGHTING_VA: { type: string; vaPerSqFt: number }[] = [
  { type: "Dwelling Units", vaPerSqFt: 3 },
  { type: "Hotels/Motels (guest rooms)", vaPerSqFt: 2 },
  { type: "Warehouses (storage)", vaPerSqFt: 0.25 },
  { type: "Office Buildings", vaPerSqFt: 3.5 },
  { type: "Retail Stores", vaPerSqFt: 1.5 },
  { type: "Restaurants", vaPerSqFt: 2 },
  { type: "Schools", vaPerSqFt: 3 },
  { type: "Hospitals", vaPerSqFt: 2 },
  { type: "Assembly / Churches", vaPerSqFt: 1 },
  { type: "Banks", vaPerSqFt: 3.5 },
  { type: "Barber/Beauty Shops", vaPerSqFt: 3 },
  { type: "Industrial / Commercial", vaPerSqFt: 2 },
];

export const LightingLoadCalc = () => {
  const [occupancyIdx, setOccupancyIdx] = useState("0");
  const [sqft, setSqft] = useState("");
  const [voltage, setVoltage] = useState("120");

  const occ = LIGHTING_VA[parseInt(occupancyIdx)];
  const area = parseFloat(sqft);
  const V = parseFloat(voltage);
  const hasInput = area > 0 && V > 0;

  const totalVA = hasInput ? occ.vaPerSqFt * area : 0;
  const amps = hasInput ? totalVA / V : 0;
  const circuits15 = Math.ceil(amps / 12); // 80% of 15A
  const circuits20 = Math.ceil(amps / 16); // 80% of 20A

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">general lighting load</span> per NEC Table 220.12. These are minimum required loads — actual connected load may differ.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Occupancy Type</label>
          <Select value={occupancyIdx} onValueChange={setOccupancyIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LIGHTING_VA.map((o, i) => (
                <SelectItem key={i} value={String(i)}>{o.type} ({o.vaPerSqFt} VA/ft²)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Floor Area (ft²)</label>
            <Input type="number" inputMode="decimal" placeholder="sq ft…" value={sqft} onChange={(e) => setSqft(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Voltage</label>
            <Select value={voltage} onValueChange={setVoltage}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="120">120V</SelectItem>
                <SelectItem value="208">208V</SelectItem>
                <SelectItem value="277">277V</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Lighting Load" value={fmt(totalVA, 0)} unit="VA" accent />
          <ResultCard label="Current" value={fmt(amps, 1)} unit="A" accent />
          <ResultCard label="VA per ft²" value={String(occ.vaPerSqFt)} unit="VA/ft²" />
          <div className="border-t border-border my-2" />
          <ResultCard label="Min. 15A Circuits" value={String(circuits15)} unit="" />
          <ResultCard label="Min. 20A Circuits" value={String(circuits20)} unit="" />
        </div>
      )}
    </div>
  );
};
